import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type {
  JourneyDefinition,
  JourneyEdge,
  JourneyNode,
  TriggerKind,
} from "@/components/journey/types";
import { activateWorkflow } from "./use-workflows";

interface WorkflowSummary {
  id: string;
  name: string;
  status: "draft" | "active" | "paused";
  createdAt: string;
  updatedAt: string;
}

interface WorkflowVersion {
  id: string;
  version: number;
  nodes: Array<{
    id: string;
    type: string;
    config: Record<string, unknown>;
    position?: { x: number; y: number };
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    label?: string;
    condition?: string;
  }>;
}

interface WorkflowListResponse {
  data: WorkflowSummary[];
}

interface WorkflowVersionResponse {
  version: WorkflowVersion;
}

interface SaveJourneyInput {
  name: string;
  description: string;
  status: string;
  nodes: JourneyNode[];
  edges: JourneyEdge[];
}

interface SaveJourneyResult {
  id: string;
}

interface SaveJourneyDeps {
  createWorkflowRecord: (input: { name: string }) => Promise<{ id: string }>;
  publishWorkflowVersion: (
    workflowId: string,
    input: {
      nodes: WorkflowVersion["nodes"];
      edges: Array<{
        id: string;
        source: string;
        target: string;
        label?: string;
      }>;
    },
  ) => Promise<void>;
  activateWorkflowRecord: (workflowId: string) => Promise<void>;
}

function isTriggerKind(value: unknown): value is TriggerKind {
  return typeof value === "string" && [
    "form_submitted",
    "tag_added",
    "deal_stage_changed",
    "event_fired",
    "segment_entered",
    "date_reached",
    "manual_entry",
  ].includes(value);
}

function deriveTriggerNodeType(node: JourneyNode): string {
  if (node.type !== "trigger") {
    return node.type;
  }

  const triggerKind = isTriggerKind((node as JourneyNode & { triggerKind?: unknown }).triggerKind)
    ? (node as JourneyNode & { triggerKind: TriggerKind }).triggerKind
    : "manual_entry";

  switch (triggerKind) {
    case "form_submitted":
      return "trigger_form";
    case "date_reached":
      return "trigger_scheduled";
    case "manual_entry":
      return "trigger_manual";
    default:
      return "trigger_event";
  }
}

function mapJourneyNodeToWorkflowNode(node: JourneyNode): WorkflowVersion["nodes"][number] {
  if (node.type === "trigger") {
    const workflowType = deriveTriggerNodeType(node);
    const triggerKind = node.triggerKind;

    if (workflowType === "trigger_form") {
      return {
        id: node.id,
        type: workflowType,
        position: node.position,
        config: {
          label: node.label,
          formId: typeof node.config.formId === "string" ? node.config.formId : "",
        },
      };
    }

    if (workflowType === "trigger_scheduled") {
      return {
        id: node.id,
        type: workflowType,
        position: node.position,
        config: {
          label: node.label,
          cron: typeof node.config.cron === "string" ? node.config.cron : "",
        },
      };
    }

    if (workflowType === "trigger_event") {
      return {
        id: node.id,
        type: workflowType,
        position: node.position,
        config: {
          label: node.label,
          eventType: typeof node.config.eventType === "string" ? node.config.eventType : triggerKind,
          triggerKind,
          ...node.config,
        },
      };
    }

    return {
      id: node.id,
      type: workflowType,
      position: node.position,
      config: {
        label: node.label,
        triggerKind,
        ...node.config,
      },
    };
  }

  const mappedType = node.type === "message"
    ? "action_send_email"
    : node.type === "wait"
      ? "condition_wait"
      : node.type === "condition"
        ? "condition_if"
        : node.type === "split"
          ? "condition_split"
          : node.type === "sms"
            ? "action_send_sms"
            : node.type === "webhook"
              ? "action_webhook"
              : node.type;

  return {
    id: node.id,
    type: mappedType,
    position: node.position,
    config: {
      label: node.label,
      ...("subject" in node ? { subject: node.subject } : {}),
      ...("body" in node ? { body: node.body } : {}),
      ...("intent" in node ? { intent: node.intent } : {}),
      ...("duration" in node ? { duration: node.duration, unit: node.unit, durationHours: node.unit === "days" ? node.duration * 24 : node.unit === "hours" ? node.duration : node.duration / 60 } : {}),
      ...("field" in node ? { field: node.field } : {}),
      ...("operator" in node ? { operator: node.operator } : {}),
      ...("value" in node ? { value: node.value } : {}),
      ...("variants" in node ? { variants: node.variants, splitPercent: node.variants[0]?.percentage ?? 50 } : {}),
      ...("url" in node ? { url: node.url } : {}),
    },
  };
}

function mapWorkflowNodeToJourneyNode(node: WorkflowVersion["nodes"][number]): JourneyNode {
  if (node.type === "trigger_form") {
    return {
      id: node.id,
      type: "trigger",
      label: String(node.config.label ?? "Form Submitted"),
      position: node.position ?? { x: 100, y: 100 },
      triggerKind: "form_submitted",
      config: node.config,
    };
  }

  if (node.type === "trigger_scheduled") {
    return {
      id: node.id,
      type: "trigger",
      label: String(node.config.label ?? "Scheduled"),
      position: node.position ?? { x: 100, y: 100 },
      triggerKind: "date_reached",
      config: node.config,
    };
  }

  if (node.type === "trigger_event") {
    return {
      id: node.id,
      type: "trigger",
      label: String(node.config.label ?? "Event Trigger"),
      position: node.position ?? { x: 100, y: 100 },
      triggerKind: isTriggerKind(node.config.triggerKind) ? node.config.triggerKind : "event_fired",
      config: node.config,
    };
  }

  if (node.type === "trigger_manual") {
    return {
      id: node.id,
      type: "trigger",
      label: String(node.config.label ?? "Manual Trigger"),
      position: node.position ?? { x: 100, y: 100 },
      triggerKind: "manual_entry",
      config: node.config,
    };
  }

  if (node.type === "action_send_email") {
    return {
      id: node.id,
      type: "message",
      label: String(node.config.label ?? "Send Email"),
      position: node.position ?? { x: 100, y: 100 },
      subject: String(node.config.subject ?? ""),
      body: String(node.config.body ?? ""),
      intent: String(node.config.intent ?? "nurture"),
    };
  }

  if (node.type === "condition_wait") {
    const durationHours = typeof node.config.durationHours === "number" ? node.config.durationHours : 24;
    return {
      id: node.id,
      type: "wait",
      label: String(node.config.label ?? "Wait"),
      position: node.position ?? { x: 100, y: 100 },
      duration: durationHours,
      unit: "hours",
    };
  }

  if (node.type === "condition_if") {
    return {
      id: node.id,
      type: "condition",
      label: String(node.config.label ?? "If / Else"),
      position: node.position ?? { x: 100, y: 100 },
      field: String(node.config.field ?? "lifecycleStage"),
      operator: "equals",
      value: String(node.config.value ?? ""),
    };
  }

  if (node.type === "condition_split") {
    return {
      id: node.id,
      type: "split",
      label: String(node.config.label ?? "Split"),
      position: node.position ?? { x: 100, y: 100 },
      variants: Array.isArray(node.config.variants)
        ? node.config.variants as Array<{ id: string; name: string; percentage: number }>
        : [
            { id: `${node.id}-a`, name: "A", percentage: Number(node.config.splitPercent ?? 50) },
            { id: `${node.id}-b`, name: "B", percentage: 100 - Number(node.config.splitPercent ?? 50) },
          ],
    };
  }

  if (node.type === "action_send_sms") {
    return {
      id: node.id,
      type: "sms",
      label: String(node.config.label ?? "Send SMS"),
      position: node.position ?? { x: 100, y: 100 },
      body: String(node.config.message ?? node.config.body ?? ""),
    };
  }

  if (node.type === "action_webhook") {
    return {
      id: node.id,
      type: "webhook",
      label: String(node.config.label ?? "Webhook"),
      position: node.position ?? { x: 100, y: 100 },
      method: "POST",
      url: String(node.config.url ?? ""),
    };
  }

  return {
    id: node.id,
    type: "message",
    label: String(node.config.label ?? node.type),
    position: node.position ?? { x: 100, y: 100 },
    subject: "",
    body: JSON.stringify({ originalType: node.type, ...node.config }),
    intent: "custom",
  };
}

function mapWorkflowToJourney(
  workflow: WorkflowSummary,
  version: WorkflowVersion | null,
): JourneyDefinition {
  return {
    id: workflow.id,
    tenantId: "mounted-workflow",
    name: workflow.name,
    description: "",
    status: workflow.status,
    nodes: version?.nodes.map(mapWorkflowNodeToJourneyNode) ?? [],
    edges: version?.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
    })) ?? [],
    createdAt: workflow.createdAt,
    updatedAt: workflow.updatedAt,
  };
}

async function defaultCreateWorkflowRecord(input: { name: string }): Promise<{ id: string }> {
  const created = await apiFetch<{ workflow: { id: string } }>("/crm/workflows", {
    method: "POST",
    body: JSON.stringify({ name: input.name }),
  });

  return { id: created.workflow.id };
}

async function defaultPublishWorkflowVersion(
  workflowId: string,
  input: {
    nodes: WorkflowVersion["nodes"];
    edges: Array<{
      id: string;
      source: string;
      target: string;
      label?: string;
    }>;
  },
): Promise<void> {
  await apiFetch(`/crm/workflows/${workflowId}/versions`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

async function saveJourneyDefinition(
  input: SaveJourneyInput,
  deps: SaveJourneyDeps,
): Promise<SaveJourneyResult> {
  const { id: workflowId } = await deps.createWorkflowRecord({ name: input.name });
  const nodes = input.nodes.map(mapJourneyNodeToWorkflowNode);
  const edges = input.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
  }));

  await deps.publishWorkflowVersion(workflowId, { nodes, edges });

  if (input.status === "active") {
    await deps.activateWorkflowRecord(workflowId);
  }

  return { id: workflowId };
}

export function useJourneys() {
  const [journeys, setJourneys] = useState<JourneyDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<WorkflowListResponse>("/crm/workflows");
      const workflows = response.data ?? [];
      const versions = await Promise.all(
        workflows.map(async (workflow) => {
          try {
            const versionResponse = await apiFetch<WorkflowVersionResponse>(`/crm/workflows/${workflow.id}/versions/latest`);
            return [workflow.id, versionResponse.version] as const;
          } catch {
            return [workflow.id, null] as const;
          }
        }),
      );

      const versionMap = new Map(versions);
      setJourneys(workflows.map((workflow) => mapWorkflowToJourney(workflow, versionMap.get(workflow.id) ?? null)));
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load journeys");
      setJourneys([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { journeys, total: journeys.length, isLoading, error, refetch: fetchData };
}

export function useSaveJourney() {
  const [isLoading, setIsLoading] = useState(false);

  const mutate = useCallback(async (input: SaveJourneyInput): Promise<SaveJourneyResult> => {
    setIsLoading(true);

    try {
      return await saveJourneyDefinition(input, {
        createWorkflowRecord: defaultCreateWorkflowRecord,
        publishWorkflowVersion: defaultPublishWorkflowVersion,
        activateWorkflowRecord: activateWorkflow,
      });
    } catch (caughtError) {
      throw caughtError instanceof Error
        ? caughtError
        : new Error("Failed to save journey");
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading };
}

export const __journeyHookInternals = {
  mapJourneyNodeToWorkflowNode,
  mapWorkflowNodeToJourneyNode,
  mapWorkflowToJourney,
  saveJourneyDefinition,
};
